import { EInvoicingConnection, SharedProps } from '@/types'
import { router, usePage } from '@inertiajs/react'
import { Alert, Badge, Button, Card, Group, Stack, Text, Title } from '@mantine/core'
import { IconAlertTriangle, IconClockHour4, IconPlugConnected, IconRefresh } from '@tabler/icons-react'
import dayjs from 'dayjs'

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="md">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={500} ta="right" style={{ wordBreak: 'break-all' }}>
        {value || '—'}
      </Text>
    </Group>
  )
}

export default function PlatformConnectionCard({
  connection,
  oauthAvailable,
}: {
  connection: EInvoicingConnection | null
  oauthAvailable: boolean
}) {
  const { current_organization } = usePage<SharedProps>().props
  const basePath = `/organizations/${current_organization!.slug}`

  const handleAuthorize = () => router.post(`${basePath}/e_invoicing_authorization`)

  const handleSynchronize = () => router.post(`${basePath}/e_invoicing_synchronization`)

  const handleDisconnect = () => {
    if (!confirm('Déconnecter Super PDP ? Les factures ne pourront plus être envoyées ni suivies.')) return
    router.delete(`${basePath}/e_invoicing_connection`)
  }

  const isConnected = !!connection?.connected_at
  const isDisconnected = !isConnected && !connection?.awaiting_verification

  return (
    <Card withBorder radius="lg" padding="xl" shadow="sm">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={4}>Plateforme agréée</Title>
            <Text size="sm" c="dimmed">
              Super PDP transmet les factures à la plateforme de chaque client et en suit le statut.
            </Text>
          </div>
          {isConnected && (
            <Badge variant="light" color={connection.environment === 'sandbox' ? 'yellow' : 'green'}>
              {connection.environment === 'sandbox' ? 'Bac à sable' : 'Production'}
            </Badge>
          )}
        </Group>

        {connection?.last_error && (
          <Alert variant="light" color="red" icon={<IconAlertTriangle />}>
            {connection.last_error}
          </Alert>
        )}

        {isConnected && (
          <>
            <Stack gap={6}>
              <Detail label="Entreprise" value={connection.platform_company_name} />
              <Detail label="Identifiant" value={connection.platform_company_number} />
              <Detail label="Adresse d'envoi" value={connection.electronic_address} />
              <Detail
                label="Dernière synchronisation"
                value={connection.last_synced_at ? dayjs(connection.last_synced_at).format('DD/MM/YYYY HH:mm') : null}
              />
            </Stack>
            <Group justify="flex-end">
              <Button variant="subtle" color="red" onClick={handleDisconnect}>
                Déconnecter
              </Button>
              <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={handleSynchronize}>
                Actualiser les statuts
              </Button>
            </Group>
          </>
        )}

        {connection?.awaiting_verification && (
          <>
            <Alert variant="light" color="blue" icon={<IconClockHour4 />}>
              {connection.verification_status === 'failed'
                ? "Super PDP n'a pas pu vérifier l'identité de l'entreprise. Reprenez la vérification sur Super PDP, puis vérifiez à nouveau."
                : "Super PDP vérifie l'identité de l'entreprise. La connexion s'activera automatiquement une fois la vérification terminée."}
            </Alert>
            <Group justify="flex-end">
              <Button variant="subtle" color="red" onClick={handleDisconnect}>
                Annuler
              </Button>
              <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={handleSynchronize}>
                Vérifier à nouveau
              </Button>
            </Group>
          </>
        )}

        {isDisconnected && oauthAvailable && (
          <Stack gap="sm">
            <Text size="sm">
              Vous serez redirigé vers Super PDP pour créer ou relier votre compte et vérifier l'identité de
              l'entreprise, puis ramené ici.
            </Text>
            <Group>
              <Button leftSection={<IconPlugConnected size={18} />} onClick={handleAuthorize}>
                Connecter avec Super PDP
              </Button>
            </Group>
          </Stack>
        )}

        {isDisconnected && !oauthAvailable && (
          <Text size="sm" c="dimmed">
            La connexion à Super PDP n'est pas encore disponible.
          </Text>
        )}
      </Stack>
    </Card>
  )
}
